/**
 * Utility functions for creating map marker icons
 */

import { DivIcon } from 'leaflet';
import { PLACE_TYPE_ICONS, CONFIDENCE_COLORS } from './map-constants';

/**
 * Icon memoization cache - stores created icons by a composite key
 * Key format: "{type}|{importance}|{relevanceType}|{confidence}"
 */
const iconCache = new Map<string, DivIcon>();

/**
 * Generate a cache key for an entity's visual properties
 * For area features (region, body-of-water, desert), include placeName
 * since the name is baked into the icon HTML
 */
function getIconCacheKey(entity: GeographicEntityWithRelevance): string {
    const areaTypes = ['region', 'body-of-water', 'desert'];
    const nameKey = areaTypes.includes(entity.type) ? `|${entity.placeName}` : '';
    return `${entity.type}|${entity.importance || 'low'}|${entity.relevanceType || 'passage-place'}|${entity.coordinateConfidence || 'high'}${nameKey}`;
}

/**
 * Memoized version of createPlaceIcon - returns cached icon if available
 * This significantly reduces DOM creation overhead for markers with identical visual properties
 */
export function createMemoizedPlaceIcon(entity: GeographicEntityWithRelevance): DivIcon {
    const cacheKey = getIconCacheKey(entity);

    let cachedIcon = iconCache.get(cacheKey);
    if (cachedIcon) {
        return cachedIcon;
    }

    // Create new icon and cache it
    const newIcon = createPlaceIcon(entity);
    iconCache.set(cacheKey, newIcon);
    return newIcon;
}

/**
 * Create a custom place icon with size and color based on importance and relevance
 * For area features (regions, bodies of water, deserts), creates text labels instead of pins
 * Routes use regular pins since they're specific points on a route, not the entire route
 */
export function createPlaceIcon(entity: GeographicEntityWithRelevance): DivIcon {
    // Special handling for area features - show as text labels instead of pins
    if (entity.type === 'region') {
        return createRegionLabel(entity);
    }
    if (entity.type === 'body-of-water') {
        return createBodyOfWaterLabel(entity);
    }
    if (entity.type === 'desert') {
        return createDesertLabel(entity);
    }
    // Note: 'route' type uses regular pins (not labels) since they represent specific points

    const icon = PLACE_TYPE_ICONS[entity.type] || '📍';

    // Determine size and border based on relevance type AND importance
    let size = 32;
    let borderColor = '#3b82f6'; // blue (default)
    let borderWidth = 2;

    if (!entity.relevanceType || entity.relevanceType === 'passage-place') {
        // PlacesByPassage entities - use importance field
        if (entity.importance === 'high') {
            size = 44;
            borderColor = '#dc2626'; // dark red
            borderWidth = 4;
        } else if (entity.importance === 'medium') {
            size = 38;
            borderColor = '#ef4444'; // red
            borderWidth = 3;
        } else { // low
            size = 34;
            borderColor = '#f87171'; // light red
            borderWidth = 2;
        }
    } else if (entity.relevanceType === 'event-related') {
        // Places connected via Place.relatedHistoricalEvents
        size = 28;
        borderColor = '#10b981'; // green
        borderWidth = 2;
    } else if (entity.relevanceType === 'general') {
        // General ancient places (when "Show All" is enabled)
        size = 24;
        borderColor = '#6b7280'; // gray
        borderWidth = 1;
    }

    const fontSize = size * 0.5;

    // Visual indicators for low confidence/disputed locations
    const confidence = entity.coordinateConfidence || 'high';
    const isUncertain = confidence === 'low' || confidence === 'disputed';
    const opacity = isUncertain ? 0.65 : 1;
    const borderStyle = isUncertain ? 'dashed' : 'solid';
    const animation = confidence === 'disputed' ? 'animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;' : '';

    return new DivIcon({
        html: `
            <style>
                @keyframes pulse {
                    0%, 100% { opacity: ${opacity}; }
                    50% { opacity: ${opacity * 0.7}; }
                }
            </style>
            <div style="font-size: ${fontSize}px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)); border: ${borderWidth}px ${borderStyle} ${borderColor}; border-radius: 50%; background: white; width: ${size}px; height: ${size}px; display: flex; align-items: center; justify-content: center; opacity: ${opacity}; ${animation}">${icon}</div>
        `,
        className: '',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
    });
}

/**
 * Create a text label for regions (no pin, just text)
 */
function createRegionLabel(entity: GeographicEntityWithRelevance): DivIcon {
    const name = entity.placeName.toUpperCase();

    // Determine styling based on relevance type and importance
    // Using earth tones for ancient map aesthetic
    let fontSize = 14;
    let fontWeight = 600;
    let color = '#8B4513'; // saddle brown (default)
    let textShadow = '0 1px 3px rgba(0,0,0,0.5), 0 0 8px rgba(255,255,255,0.8)';

    if (!entity.relevanceType || entity.relevanceType === 'passage-place') {
        // PlacesByPassage entities - use importance field
        if (entity.importance === 'high') {
            fontSize = 16;
            fontWeight = 700;
            color = '#8B4513'; // saddle brown - like aged ink
        } else if (entity.importance === 'medium') {
            fontSize = 15;
            fontWeight = 600;
            color = '#CD853F'; // peru/tan - like faded parchment writing
        } else { // low
            fontSize = 14;
            fontWeight = 600;
            color = '#D2B48C'; // tan - subtle, weathered
        }
    } else if (entity.relevanceType === 'event-related') {
        fontSize = 13;
        color = '#556B2F'; // dark olive green - like oxidized bronze
    } else if (entity.relevanceType === 'general') {
        fontSize = 12;
        fontWeight = 500;
        color = '#A0826D'; // taupe - neutral earth tone
    }

    return new DivIcon({
        html: `<div style="
            font-size: ${fontSize}px; 
            font-weight: ${fontWeight}; 
            color: ${color}; 
            text-shadow: ${textShadow};
            white-space: nowrap;
            pointer-events: auto;
            cursor: pointer;
            letter-spacing: 0.5px;
        ">${name}</div>`,
        className: '',
        iconSize: [0, 0], // No fixed size for text
        iconAnchor: [0, 0] // Anchor at top-left of text
    });
}

/**
 * Create a text label for bodies of water (seas, lakes, rivers)
 */
function createBodyOfWaterLabel(entity: GeographicEntityWithRelevance): DivIcon {
    const name = entity.placeName.toUpperCase();

    // Water features use blue tones - IMPROVED LEGIBILITY
    // Increased size, darker color for contrast, and strong white glow
    let fontSize = 15;
    let fontWeight = 700;
    let color = '#1e3a8a'; // dark blue (blue-900) for better contrast
    // Strong white glow to separate from map background
    let textShadow = '0 0 4px #ffffff, 0 0 8px #ffffff, 0 0 12px #ffffff';
    let fontStyle = 'italic'; // Italic for water features (cartographic convention)

    if (!entity.relevanceType || entity.relevanceType === 'passage-place') {
        if (entity.importance === 'high') {
            fontSize = 18; // Larger for major seas
            fontWeight = 800;
            color = '#172554'; // very dark blue (blue-950)
        } else if (entity.importance === 'medium') {
            fontSize = 16;
            fontWeight = 700;
            color = '#1e3a8a'; // blue-900
        } else { // low
            fontSize = 14;
            fontWeight = 600;
            color = '#1e40af'; // blue-800
        }
    } else if (entity.relevanceType === 'event-related') {
        fontSize = 14;
        color = '#0e7490'; // cyan-700
    } else if (entity.relevanceType === 'general') {
        fontSize = 13;
        fontWeight = 600;
        color = '#1d4ed8'; // blue-700
    }

    // Visual indicators for low confidence/disputed locations
    const confidence = entity.coordinateConfidence || 'high';
    const isUncertain = confidence === 'low' || confidence === 'disputed';
    const opacity = isUncertain ? 0.75 : 1;
    const animation = confidence === 'disputed' ? 'animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;' : '';

    return new DivIcon({
        html: `
            <style>
                @keyframes pulse {
                    0%, 100% { opacity: ${opacity}; }
                    50% { opacity: ${opacity * 0.7}; }
                }
            </style>
            <div style="
                font-size: ${fontSize}px; 
                font-weight: ${fontWeight}; 
                font-style: ${fontStyle};
                color: ${color}; 
                text-shadow: ${textShadow};
                white-space: nowrap;
                pointer-events: auto;
                cursor: pointer;
                letter-spacing: 1px;
                opacity: ${opacity};
                ${animation}
                font-family: serif;
                transform: translateY(-50%);
            ">${name}</div>
        `,
        className: '',
        iconSize: [0, 0],
        iconAnchor: [0, 0]
    });
}

/**
 * Create a text label for deserts
 */
function createDesertLabel(entity: GeographicEntityWithRelevance): DivIcon {
    const name = entity.placeName.toUpperCase();

    // Desert features use sandy/tan tones
    let fontSize = 14;
    let fontWeight = 600;
    let color = '#DEB887'; // burlywood (default)
    let textShadow = '0 1px 3px rgba(0,0,0,0.5), 0 0 8px rgba(255,255,255,0.8)';

    if (!entity.relevanceType || entity.relevanceType === 'passage-place') {
        if (entity.importance === 'high') {
            fontSize = 15;
            fontWeight = 700;
            color = '#D2691E'; // chocolate - prominent desert
        } else if (entity.importance === 'medium') {
            fontSize = 14;
            fontWeight = 600;
            color = '#DEB887'; // burlywood
        } else { // low
            fontSize = 13;
            fontWeight = 500;
            color = '#F5DEB3'; // wheat - subtle
        }
    } else if (entity.relevanceType === 'event-related') {
        fontSize = 13;
        color = '#CD853F'; // peru
    } else if (entity.relevanceType === 'general') {
        fontSize = 12;
        fontWeight = 500;
        color = '#E6D5B8'; // light tan
    }

    // Visual indicators for low confidence/disputed locations
    const confidence = entity.coordinateConfidence || 'high';
    const isUncertain = confidence === 'low' || confidence === 'disputed';
    const opacity = isUncertain ? 0.65 : 1;
    const animation = confidence === 'disputed' ? 'animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;' : '';

    return new DivIcon({
        html: `
            <style>
                @keyframes pulse {
                    0%, 100% { opacity: ${opacity}; }
                    50% { opacity: ${opacity * 0.7}; }
                }
            </style>
            <div style="
                font-size: ${fontSize}px; 
                font-weight: ${fontWeight}; 
                color: ${color}; 
                text-shadow: ${textShadow};
                white-space: nowrap;
                pointer-events: auto;
                cursor: pointer;
                letter-spacing: 0.5px;
                opacity: ${opacity};
                ${animation}
            ">${name}</div>
        `,
        className: '',
        iconSize: [0, 0],
        iconAnchor: [0, 0]
    });
}

/**
 * Create a text label for routes (trade routes, roads, paths)
 */
function createRouteLabel(entity: GeographicEntityWithRelevance): DivIcon {
    const name = entity.placeName.toUpperCase();

    // Routes use neutral/path tones
    let fontSize = 13;
    let fontWeight = 600;
    let color = '#8B7355'; // brown-gray (default)
    let textShadow = '0 1px 3px rgba(0,0,0,0.5), 0 0 8px rgba(255,255,255,0.8)';
    let fontStyle = 'normal';

    if (!entity.relevanceType || entity.relevanceType === 'passage-place') {
        if (entity.importance === 'high') {
            fontSize = 14;
            fontWeight = 700;
            color = '#704214'; // dark brown - major route
        } else if (entity.importance === 'medium') {
            fontSize = 13;
            fontWeight = 600;
            color = '#8B7355'; // brown-gray
        } else { // low
            fontSize = 12;
            fontWeight = 500;
            color = '#A0826D'; // taupe - minor route
        }
    } else if (entity.relevanceType === 'event-related') {
        fontSize = 12;
        color = '#6B5D4F'; // dark taupe
    } else if (entity.relevanceType === 'general') {
        fontSize = 11;
        fontWeight = 500;
        color = '#9C8B7A'; // light brown-gray
    }

    return new DivIcon({
        html: `<div style="
            font-size: ${fontSize}px; 
            font-weight: ${fontWeight}; 
            font-style: ${fontStyle};
            color: ${color}; 
            text-shadow: ${textShadow};
            white-space: nowrap;
            pointer-events: auto;
            cursor: pointer;
            letter-spacing: 1px;
        ">${name}</div>`,
        className: '',
        iconSize: [0, 0],
        iconAnchor: [0, 0]
    });
}

/**
 * Create a custom event icon
 */
export function createEventIcon(): DivIcon {
    return new DivIcon({
        html: `<div style="font-size: 20px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)); border: 2px solid #ef4444; border-radius: 50%; background: white; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;">🕐</div>`,
        className: '',
        iconSize: [32, 32],
        iconAnchor: [16, 16]
    });
}

/**
 * Create a simple place icon with confidence-based coloring (for AncientWorldMap)
 */
export function createSimplePlaceIcon(place: Place): DivIcon {
    // Special handling for area features - show as text labels (but not routes)
    if (place.placeName) {
        const name = place.placeName.toUpperCase();
        const textShadow = '0 1px 3px rgba(0,0,0,0.5), 0 0 8px rgba(255,255,255,0.8)';

        // Region
        if (place.type === 'region') {
            const color = CONFIDENCE_COLORS[place.coordinates.confidence || 'medium'];
            const confidence = place.coordinates.confidence || 'medium';
            const isUncertain = confidence === 'low' || confidence === 'disputed';
            const opacity = isUncertain ? 0.65 : 1;
            const animation = confidence === 'disputed' ? 'animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;' : '';

            return new DivIcon({
                html: `
                    <style>
                        @keyframes pulse {
                            0%, 100% { opacity: ${opacity}; }
                            50% { opacity: ${opacity * 0.7}; }
                        }
                    </style>
                    <div style="
                        font-size: 14px;
                        font-weight: 600;
                        color: ${color};
                        text-shadow: ${textShadow};
                        white-space: nowrap;
                        pointer-events: auto;
                        cursor: pointer;
                        letter-spacing: 0.5px;
                        opacity: ${opacity};
                        ${animation}
                    ">${name}</div>
                `,
                className: '',
                iconSize: [0, 0],
                iconAnchor: [0, 0]
            });
        }

        // Body of water (italic blue)
        if (place.type === 'body-of-water') {
            const confidence = place.coordinates.confidence || 'medium';
            const isUncertain = confidence === 'low' || confidence === 'disputed';
            const opacity = isUncertain ? 0.65 : 1;
            const animation = confidence === 'disputed' ? 'animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;' : '';

            return new DivIcon({
                html: `
                    <style>
                        @keyframes pulse {
                            0%, 100% { opacity: ${opacity}; }
                            50% { opacity: ${opacity * 0.7}; }
                        }
                    </style>
                    <div style="
                        font-size: 14px; 
                        font-weight: 600; 
                        font-style: italic;
                        color: #4682B4; 
                        text-shadow: ${textShadow};
                        white-space: nowrap;
                        pointer-events: auto;
                        cursor: pointer;
                        letter-spacing: 0.5px;
                        opacity: ${opacity};
                        ${animation}
                    ">${name}</div>
                `,
                className: '',
                iconSize: [0, 0],
                iconAnchor: [0, 0]
            });
        }

        // Desert (sandy tan)
        if (place.type === 'desert') {
            const confidence = place.coordinates.confidence || 'medium';
            const isUncertain = confidence === 'low' || confidence === 'disputed';
            const opacity = isUncertain ? 0.65 : 1;
            const animation = confidence === 'disputed' ? 'animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;' : '';

            return new DivIcon({
                html: `
                    <style>
                        @keyframes pulse {
                            0%, 100% { opacity: ${opacity}; }
                            50% { opacity: ${opacity * 0.7}; }
                        }
                    </style>
                    <div style="
                        font-size: 14px; 
                        font-weight: 600; 
                        color: #DEB887; 
                        text-shadow: ${textShadow};
                        white-space: nowrap;
                        pointer-events: auto;
                        cursor: pointer;
                        letter-spacing: 0.5px;
                        opacity: ${opacity};
                        ${animation}
                    ">${name}</div>
                `,
                className: '',
                iconSize: [0, 0],
                iconAnchor: [0, 0]
            });
        }

        // Note: 'route' type falls through to use regular pin icon below
    }

    const icon = PLACE_TYPE_ICONS[place.type] || '📍';
    const color = CONFIDENCE_COLORS[place.coordinates.confidence || 'medium'];

    // Visual indicators for low confidence/disputed locations
    const confidence = place.coordinates.confidence || 'medium';
    const isUncertain = confidence === 'low' || confidence === 'disputed';
    const opacity = isUncertain ? 0.65 : 1;
    const borderStyle = isUncertain ? 'dashed' : 'solid';
    const animation = confidence === 'disputed' ? 'animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;' : '';

    return new DivIcon({
        html: `
            <style>
                @keyframes pulse {
                    0%, 100% { opacity: ${opacity}; }
                    50% { opacity: ${opacity * 0.7}; }
                }
            </style>
            <div style="font-size: 24px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)); border: 3px ${borderStyle} ${color}; border-radius: 50%; background: white; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; opacity: ${opacity}; ${animation}">${icon}</div>
        `,
        className: '',
        iconSize: [36, 36],
        iconAnchor: [18, 18]
    });
}

